const state = {
    // 全局加载状态
    loading: false,

    // 网络状态
    networkStatus: navigator.onLine,

    // 应用设置
    settings: {
        theme: 'light', // light, dark
        language: 'zh-CN',
        rememberLogin: true,
        autoRedirect: true
    },

    // 全局消息提示
    messages: [],

    // 消息通知
    notifications: [],

    // 设备信息
    device: {
        isMobile: false,
        isTablet: false,
        isDesktop: true
    },

    // 错误信息
    errors: []
}

const getters = {
    loading: state => state.loading,
    networkStatus: state => state.networkStatus,
    settings: state => state.settings,
    messages: state => state.messages,
    notifications: state => state.notifications,
    device: state => state.device,
    errors: state => state.errors,

    // 是否为移动设备
    isMobile: state => state.device.isMobile,

    // 当前主题
    currentTheme: state => state.settings.theme,

    // 未读通知数量
    unreadNotificationCount: state => {
        return state.notifications.filter(n => !n.read).length
    }
}

const mutations = {
    // 设置加载状态
    SET_LOADING(state, status) {
        state.loading = status
    },

    // 设置网络状态
    SET_NETWORK_STATUS(state, status) {
        state.networkStatus = status
    },

    // 更新设置
    UPDATE_SETTINGS(state, settings) {
        state.settings = { ...state.settings, ...settings }
    },

    // 设置主题
    SET_THEME(state, theme) {
        state.settings.theme = theme
        // 更新document class
        document.documentElement.className = `theme-${theme}`
    },

    // 设置语言
    SET_LANGUAGE(state, language) {
        state.settings.language = language
    },

    // 添加全局消息
    ADD_MESSAGE(state, message) {
        const id = Date.now() + Math.random()
        state.messages.push({
            id,
            type: 'info',
            autoClose: true,
            duration: 3000,
            ...message
        })
    },

    // 移除消息
    REMOVE_MESSAGE(state, id) {
        state.messages = state.messages.filter(m => m.id !== id)
    },

    // 清除所有消息
    CLEAR_MESSAGES(state) {
        state.messages = []
    },

    // 添加通知
    ADD_NOTIFICATION(state, notification) {
        const id = Date.now() + Math.random()
        state.notifications.unshift({
            id,
            read: false,
            timestamp: new Date(),
            ...notification
        })

        // 限制通知数量
        if (state.notifications.length > 50) {
            state.notifications = state.notifications.slice(0, 50)
        }
    },

    // 标记通知为已读
    MARK_NOTIFICATION_READ(state, id) {
        const notification = state.notifications.find(n => n.id === id)
        if (notification) {
            notification.read = true
        }
    },

    // 删除通知
    REMOVE_NOTIFICATION(state, id) {
        state.notifications = state.notifications.filter(n => n.id !== id)
    },

    // 清除所有通知
    CLEAR_NOTIFICATIONS(state) {
        state.notifications = []
    },

    // 设置设备信息
    SET_DEVICE_INFO(state, deviceInfo) {
        state.device = { ...state.device, ...deviceInfo }
    },

    // 添加错误
    ADD_ERROR(state, error) {
        const id = Date.now() + Math.random()
        state.errors.push({
            id,
            timestamp: new Date(),
            ...error
        })

        // 限制错误数量
        if (state.errors.length > 20) {
            state.errors = state.errors.slice(0, 20)
        }
    },

    // 清除错误
    CLEAR_ERRORS(state) {
        state.errors = []
    },

    // 重置应用状态
    RESET_SETTINGS(state) {
        state.settings = {
            theme: 'light',
            language: 'zh-CN',
            rememberLogin: true,
            autoRedirect: true
        }
        state.notifications = []
        state.errors = []
    }
}

const actions = {
    // 初始化应用设置
    async initSettings({ commit, dispatch }) {
        try {
            // 从localStorage加载设置
            const savedSettings = localStorage.getItem('auth_portal_settings')
            if (savedSettings) {
                const settings = JSON.parse(savedSettings)
                commit('UPDATE_SETTINGS', settings)
                commit('SET_THEME', settings.theme || 'light')
            }

            // 检测设备类型
            await dispatch('detectDevice')

            console.log('✅ 应用设置初始化完成')
        } catch (error) {
            console.error('❌ 应用设置初始化失败:', error)
        }
    },

    // 检测设备类型
    async detectDevice({ commit }) {
        const userAgent = navigator.userAgent.toLowerCase()
        const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent)
        const isTablet = /ipad|android(?!.*mobile)/i.test(userAgent)
        const isDesktop = !isMobile && !isTablet

        commit('SET_DEVICE_INFO', {
            isMobile,
            isTablet,
            isDesktop
        })
    },

    // 更新设置
    async updateSettings({ commit, state }, settings) {
        commit('UPDATE_SETTINGS', settings)

        // 保存到localStorage
        try {
            localStorage.setItem('auth_portal_settings', JSON.stringify(state.settings))
        } catch (error) {
            console.error('保存设置失败:', error)
        }
    },

    // 切换主题
    async toggleTheme({ commit, dispatch, state }) {
        const newTheme = state.settings.theme === 'light' ? 'dark' : 'light'
        commit('SET_THEME', newTheme)

        // 保存设置
        await dispatch('updateSettings', { theme: newTheme })
    },

    // 显示通知
    showNotification({ commit }, { type = 'info', title, message, duration = 5000 }) {
        const notification = {
            type, // success, warning, error, info
            title,
            message,
            duration
        }

        commit('ADD_NOTIFICATION', notification)

        // 自动删除通知
        if (duration > 0) {
            setTimeout(() => {
                commit('REMOVE_NOTIFICATION', notification.id)
            }, duration)
        }

        return notification.id
    },

    // 显示成功消息
    showSuccess({ dispatch }, { title = '成功', message }) {
        return dispatch('showNotification', {
            type: 'success',
            title,
            message
        })
    },

    // 显示错误消息
    showError({ dispatch }, { title = '错误', message }) {
        return dispatch('showNotification', {
            type: 'error',
            title,
            message,
            duration: 8000 // 错误消息显示时间长一些
        })
    },

    // 显示警告消息
    showWarning({ dispatch }, { title = '警告', message }) {
        return dispatch('showNotification', {
            type: 'warning',
            title,
            message
        })
    },

    // 记录错误
    logError({ commit }, error) {
        commit('ADD_ERROR', {
            message: error.message,
            stack: error.stack,
            url: window.location.href,
            userAgent: navigator.userAgent
        })
    },

    // 设置全局加载状态
    setLoading({ commit }, status) {
        commit('SET_LOADING', status)
    }
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
} 